import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Checkbox, IconButton } from '../..';
import _ from 'lodash';
import {
    List,
    ListItem,
    ListItemIcon,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    ListItemText
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { withStyles } from '@material-ui/core/styles';
import { getConnexionName } from 'src/utils/externalIntegration';
import { styles } from '../CampaignPromo.styles';
import { mainColor } from '../../../constants';

const StyledAccordion = withStyles({
    root: {
        border: 'none',
        boxShadow: 'none',
        margin: '0 !important',
        padding: '0',
        '&:before': {
            display: 'none',
        },
        '&$expanded': {
            margin: '0 !important',
        },
    },
    expanded: {},
})(Accordion);

const StyledAccordionSummary = withStyles({
    root: {
        minHeight: '48px !important',
        height: '48px',
        padding: '0 12px',
        '&$expanded': {
            minHeight: '48px !important',
            height: '48px',
        },
    },
    content: {
        margin: '0 !important',
        '&$expanded': {
            margin: '0 !important',
        },
        alignItems: 'center',
    },
    expanded: {},
})(AccordionSummary);


const CampaignRestaurantsModal = ({
    classes,
    showModal,
    shops,
    loading,
    onClick,
    text,
    title,
    handleClose,
    choosedShops
}) => {
    const [expanded, setExpanded] = useState<string | false>(false);
    const [selectedShops, setSelectedShops] = useState<string[]>(choosedShops);
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [manuallySelectedGroups, setManuallySelectedGroups] = useState<string[]>([]);

    const shopsToSync = shops;
    const shopsWithIndex = _.map(shopsToSync, (shop, index) => ({
        ...shop,
        index,
    }));
    const groupedShops = _.groupBy(shopsWithIndex, (shop) => getConnexionName(shop, shop?.index, null));

    useEffect(() => {
        const fullySelectedGroup = Object.keys(groupedShops).find(groupName => {
            const groupShopIds = groupedShops[groupName].map(shop => shop.index);
            return groupShopIds.every(id => selectedShops.includes(id));
        });

        if (fullySelectedGroup && !manuallySelectedGroups.includes(fullySelectedGroup)) {
            setSelectedGroup(fullySelectedGroup);
        }
    }, [selectedShops, groupedShops, manuallySelectedGroups]);

    const handleAccordionToggle = (group: string) => {
        setExpanded(expanded === group ? false : group);
    };

    const handleShopToggle = (shopId: string, groupName: string) => {
        setSelectedShops(prevSelected => {
            const newSelected = prevSelected.includes(shopId)
                ? prevSelected.filter(id => id !== shopId)
                : [...prevSelected, shopId];

            if (selectedGroup === groupName && !newSelected.includes(shopId)) {
                setSelectedGroup(null);
            }

            return newSelected;
        });
    };

    const handleGroupToggle = (groupName: string) => {
        const groupShopIds = groupedShops[groupName].map(shop => shop.index);

        setSelectedShops(prevSelected => {
            const allSelected = groupShopIds.every(id => prevSelected.includes(id));

            if (allSelected) {
                setSelectedGroup(null);
                setManuallySelectedGroups(prev => prev.filter(g => g !== groupName));
                return prevSelected.filter(id => !groupShopIds.includes(id));
            } else {
                setSelectedGroup(groupName);
                setManuallySelectedGroups(prev => [...prev, groupName]);
                return [...new Set([...prevSelected, ...groupShopIds])];
            }
        });
    };

    const isGroupSelected = (groupName: string) => {
        const groupShopIds = groupedShops[groupName].map(shop => shop.index);
        return groupShopIds.every(id => selectedShops.includes(id));
    };

    const isGroupDisabled = (groupName: string) => {
        return (selectedGroup !== null && selectedGroup !== groupName) ||
            (selectedShops.length > 0 && !selectedShops.some(shopId =>
                groupedShops[groupName].some(shop => shop.index === shopId)
            ))
    };

    return (
        <Modal
            color="secondary"
            title={title}
            header
            fixed
            size="md"
            loading={loading}
            btnTxt="Valider la sélection"
            modalBodyClass="py-0"
            onClick={() => onClick(selectedShops)}
            isOpen={showModal}
            handleClose={handleClose}
        >
            <p className={classes.titleStyle}>{text}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bolder' }}>Restaurants ciblés</p>

            {Object.keys(groupedShops).map((groupName, index) => {
                const group = groupName.split('-')[0];
                const version = groupName.split('-')[1];
                const isLastItem = _.size(groupedShops) - 1 === index;
                const groupSelected = isGroupSelected(groupName);
                const groupDisabled = isGroupDisabled(groupName);

                return (
                    <React.Fragment key={groupName}>
                        <StyledAccordion expanded={expanded === groupName} onChange={() => handleAccordionToggle(groupName)}>
                            <StyledAccordionSummary
                                style={{ padding: 0 }}
                                expandIcon={
                                    <IconButton
                                        style={{ color: groupDisabled ? '#ccc' : mainColor.colorBlack }}
                                        onClick={() => handleAccordionToggle(groupName)}
                                        disabled={groupDisabled}
                                    >
                                        <ExpandMoreIcon />
                                    </IconButton>
                                }
                            >
                                <ListItem className={classes.groupStyle}>
                                    <ListItemIcon>
                                        <Checkbox
                                            value={groupSelected}
                                            inputProps={{ 'aria-labelledby': groupName }}
                                            text={
                                                <div className={classes.flexContainer}>
                                                    <p style={{
                                                        color: groupDisabled ? '#ccc' : mainColor.colorBlack,
                                                        fontSize: '16px'
                                                    }}>
                                                        {`Groupe ${index + 1}`}
                                                    </p>
                                                    <small className="ml-2" style={{ color: groupDisabled ? '#ccc' : 'inherit' }}>
                                                        {`${groupedShops[groupName].length} /${_.size(shops)} restaurants`}
                                                    </small>
                                                </div>
                                            }
                                            className="pt-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!groupDisabled) {
                                                    handleGroupToggle(groupName);
                                                }
                                            }}
                                            disabled={groupDisabled}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        style={{ color: groupDisabled ? '#ccc' : mainColor.colorBlack }}
                                        secondary={
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <span className={classes.textStyle} style={{ color: groupDisabled ? '#ccc' : 'inherit' }}>
                                                    {`Intégration: ${group}`}
                                                </span>
                                                {version && (
                                                    <span className={classes.textStyle} style={{ color: groupDisabled ? '#ccc' : 'inherit' }}>
                                                        {`Version de menu ${version}`}
                                                    </span>
                                                )}
                                            </div>
                                        }
                                    />
                                </ListItem>
                            </StyledAccordionSummary>

                            <Divider style={{ backgroundColor: 'rgba(0, 0, 0, 0.12)' }} />

                            <AccordionDetails style={{
                                padding: 0,
                                height: _.size(groupedShops[groupName]) > 4 ? '210px' : 'auto',
                                overflow: 'auto',
                                opacity: groupDisabled ? 0.6 : 1
                            }}>
                                <List style={{ width: '100%', padding: 0 }}>
                                    {groupedShops[groupName].map((shop) => (
                                        <React.Fragment key={shop.id}>
                                            <ListItem
                                                button
                                                onClick={() => !groupDisabled && handleShopToggle(shop.index, groupName)}
                                                style={{ padding: '0px 0px 0px 17px' }}
                                                disabled={groupDisabled}
                                            >
                                                <ListItemIcon style={{ minWidth: '42px' }}>
                                                    <Checkbox
                                                        value={selectedShops.includes(shop.index)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!groupDisabled) {
                                                                handleShopToggle(shop.index, groupName);
                                                            }
                                                        }}
                                                        disabled={groupDisabled}
                                                    />
                                                </ListItemIcon>
                                                <Typography style={{
                                                    color: groupDisabled ? '#ccc' : mainColor.colorBlack,
                                                    fontSize: '15px'
                                                }}>
                                                    {shop.name}
                                                </Typography>
                                            </ListItem>
                                            <Divider style={{ backgroundColor: 'rgba(0, 0, 0, 0.06)' }} />
                                        </React.Fragment>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </StyledAccordion>
                        {expanded !== groupName && !isLastItem && <Divider style={{ backgroundColor: 'rgba(0, 0, 0, 0.12)' }} />}
                    </React.Fragment>
                );
            })}
        </Modal>
    );
};

CampaignRestaurantsModal.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CampaignRestaurantsModal);